/**
 * PublicTaskDetail
 *
 * Read-only reuse of the authenticated task detail body for public shares.
 * Mirrors `TaskDetailContent`'s `DetailPanelLayout` + `TaskDetailsContent`
 * composition. Requirements are always visible; comments / attachments are
 * gated by the share's flags; history stays hidden. Related tasks and linked
 * notes are always listed — relations covered by the share open in navigation
 * tabs, the rest render non-clickable. Attachments stream through the
 * token-scoped public endpoint via `shareToken`.
 *
 * @module components/share/public/PublicArtifactDetail/parts/PublicTaskDetail
 */
import { memo, useCallback, useMemo } from 'react';

import { DetailPanelLayout } from '@/components/common/DetailPanelLayout';
import {
  TaskDetailsContent,
  TaskDetailsHeader,
  FullscreenRightPanel,
  useTaskDetailsState,
  useTaskDetailsSectionTabs,
} from '@/components/tasks/TaskDetailsPanel';
import type { TabType } from '@/components/tasks/TaskDetailsPanel';
import type { NonNavigableNote } from '@/components/tasks/panel/TaskLinkedNotes';
import { mapPublicNoteListItemToNote } from '@/store/mappers/publicShareMapper';
import { taskHasComments } from '@/utils/taskHelpers';
import type { RelatedTaskSummary, Task } from '@/types';
import type { Note } from '@/types/notes';
import type {
  PublicShareConfig,
  PublicShareListData,
  PublicTask,
} from '@/types/share';

import type { PublicNavigationTarget } from '../../usePublicDetailNavigation';

interface PublicTaskDetailProps {
  task: Task;
  /** Raw public DTO — carries linked-note references incl. out-of-share ones. */
  publicTask: PublicTask;
  /** Slim share list — decides which relations are navigable. */
  listData: PublicShareListData;
  config: PublicShareConfig;
  token: string;
  /** Desktop fullscreen → sidebar layout (mirrors authenticated view). */
  showSidebar: boolean;
  /** Wide fullscreen → right panel with mini-map + comments. */
  showRightPanel: boolean;
  onViewTask: (target: PublicNavigationTarget) => void;
  onViewNote: (target: PublicNavigationTarget) => void;
}

export const PublicTaskDetail = memo(function PublicTaskDetail({
  task,
  publicTask,
  listData,
  config,
  token,
  showSidebar,
  showRightPanel,
  onViewTask,
  onViewNote,
}: PublicTaskDetailProps) {
  // The task's linked notes split by whether the referenced note is in the
  // share's slim list. In-share notes render as full, clickable NoteCards
  // (built from the slim list item); the rest stay visible but non-clickable.
  const { inShareNotes, nonNavigableNotes } = useMemo(() => {
    const navigable: Note[] = [];
    const nonNavigable: NonNavigableNote[] = [];

    for (const linkedNote of publicTask.linkedNotes) {
      const listItem = listData.notes.find(
        (note) =>
          note.id === linkedNote.id || note.shortId === linkedNote.shortId
      );
      if (listItem) {
        navigable.push(mapPublicNoteListItemToNote(listItem));
      } else {
        nonNavigable.push({
          id: linkedNote.id,
          title: linkedNote.title,
          color: linkedNote.color,
        });
      }
    }

    return { inShareNotes: navigable, nonNavigableNotes: nonNavigable };
  }, [publicTask.linkedNotes, listData.notes]);

  const hasNotes = inShareNotes.length > 0 || nonNavigableNotes.length > 0;

  // Related tasks are navigable only when present in the share dataset.
  const shareTaskKeys = useMemo(() => {
    const keys = new Set<string>();
    listData.tasks.forEach((shareTask) => {
      keys.add(shareTask.id);
      keys.add(shareTask.shortId);
    });
    return keys;
  }, [listData.tasks]);

  const isTaskNavigable = useCallback(
    (relatedTask: RelatedTaskSummary) =>
      shareTaskKeys.has(relatedTask.id) ||
      shareTaskKeys.has(relatedTask.shortId),
    [shareTaskKeys]
  );

  const handleViewRelatedTask = useCallback(
    (shortId: string, title: string) => {
      const summary = task.relatedTasks.find(
        (relatedTask) => relatedTask.shortId === shortId
      );
      onViewTask({ id: summary?.id ?? shortId, shortId, title });
    },
    [task.relatedTasks, onViewTask]
  );

  // Comments are available only when the share allows them AND any exist.
  const commentsAvailable = config.showComments && taskHasComments(task);

  const { activeTab, setActiveTab } = useTaskDetailsState(task.id, task, {
    // Comments move to the right panel when it is visible, so drop the tab.
    hideComments: !commentsAvailable || showRightPanel,
    hideNotes: !hasNotes,
  });

  const hasAttachments =
    config.showAttachments && (task.attachments?.length ?? 0) > 0;
  const hasRequirements = (task.requirements?.length ?? 0) > 0;
  const hasGeometries = (task.geometries?.length ?? 0) > 0;

  const sectionItems = useTaskDetailsSectionTabs({
    showRightPanel,
    hasNotes,
    notesLoading: false,
    hasAttachments,
    hasRequirements,
    hasRelatedTasks: task.relatedTasks.length > 0,
    hasGeometries,
    hasComments: commentsAvailable,
    includeHistory: false,
  });

  const handleSectionChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId as TabType);
    },
    [setActiveTab]
  );

  return (
    <DetailPanelLayout
      mode={showSidebar ? 'sidebar' : 'tabs'}
      sectionItems={sectionItems}
      activeSection={activeTab}
      onSectionChange={handleSectionChange}
      sidebarHeader={
        showSidebar ? <TaskDetailsHeader task={task} /> : undefined
      }
      aside={
        showRightPanel ? (
          <FullscreenRightPanel
            task={task}
            readOnly
            showComments={commentsAvailable}
          />
        ) : undefined
      }
      contentVariant={activeTab === 'comments' ? 'wide' : 'default'}
      widePreview
    >
      <TaskDetailsContent
        activeTab={activeTab}
        task={task}
        setActiveTab={setActiveTab}
        readOnly
        shareToken={token}
        onViewRelatedTask={handleViewRelatedTask}
        onViewNote={onViewNote}
        providedNotes={inShareNotes}
        nonNavigableNotes={nonNavigableNotes}
        isTaskNavigable={isTaskNavigable}
      />
    </DetailPanelLayout>
  );
});
